import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useFormikContext } from 'formik';
import { useCallback, useMemo, memo, useEffect, useState, useRef } from 'react';
import { FaPlus } from 'react-icons/fa';
import Tab from '../../../../elements/Tab/Tab';
import Tabs from '../../../../elements/Tab/Tabs';
import useSpectrum from '../../../../hooks/useSpectrum';
import Events from '../../../../utility/Events';
import { formatNumber } from '../../../../utility/formatNumber';
import AddSignalFormTab from './AddSignalFormTab';
import DeltaInput from './DeltaInput';
import { SignalFormInfo } from './SignalFormInfo';
import SignalFormTab from './SignalFormTab';
const tabStylesAddition = css `
  color: red;
`;
const tabStyles = css `
  padding: 0 1.5rem !important;
  height: 40px;
`;
const newTabStyles = css `
  padding: 0 !important;
  height: 40px;
`;
function SignalsForm({ range, preferences }) {
    const newSignalFormRef = useRef();
    const [activeField, setActiveField] = useState(null);
    const { values, setFieldValue, errors, } = useFormikContext();
    const { info } = useSpectrum({ info: {} });
    useEffect(() => {
        function handle(event) {
            if (info?.originFrequency && activeField) {
                if (values.activeTab === 'addSignalTab') {
                    newSignalFormRef.current.setValues({
                        [activeField]: (event.range[1] - event.range[0]) / 2 + event.range[0],
                    });
                }
                else if (activeField.includes('delta')) {
                    setFieldValue(activeField, (event.range[1] - event.range[0]) / 2 + event.range[0]);
                }
                else {
                    const value = Number(formatNumber(Math.abs(event.range[0] - event.range[1]) * info.originFrequency, preferences.deltaHz.format));
                    setFieldValue(activeField, value);
                }
            }
            setActiveField(null);
        }
        Events.on('brushEnd', handle);
        return () => {
            Events.off('brushEnd', handle);
        };
    }, [
        activeField,
        setFieldValue,
        values.activeTab,
        info,
        preferences?.deltaHz?.format,
    ]);
    useEffect(() => {
        function handle(event) {
            if (activeField) {
                if (values.activeTab === 'addSignalTab') {
                    newSignalFormRef.current.setValues({ [activeField]: event.xPPM });
                }
                else if (activeField.includes('delta')) {
                    setFieldValue(activeField, event.xPPM);
                }
            }
            setActiveField(null);
        }
        Events.on('mouseClick', handle);
        return () => {
            Events.off('mouseClick', handle);
        };
    }, [values.activeTab, activeField, setFieldValue]);
    const handleOnFocus = useCallback((event) => {
        setActiveField(event.target.name);
    }, [setActiveField]);
    const tapClickHandler = useCallback(({ tabid }) => {
        setFieldValue('activeTab', tabid);
    }, [setFieldValue]);
    const handleDeleteSignal = useCallback(({ tabid }) => {
        const _signals = values.signals.filter((_signal, i) => i !== Number(tabid));
        setFieldValue('signals', _signals);
    }, [setFieldValue, values.signals]);
    useEffect(() => {
        setFieldValue('activeTab', values.signals.length > 0
            ? (values.signals.length - 1).toString()
            : 'addSignalTab');
    }, [setFieldValue, values.signals.length]);
    const tabContainsErrors = useCallback((i) => {
        return !!(errors?.signals && errors?.signals[i]);
    }, [errors]);
    const signalFormTabs = useMemo(() => {
        const signalTabs = values.signals.length > 0
            ? values.signals.map((signal, i) => (_jsx(Tab
            // eslint-disable-next-line react/no-array-index-key
            , { tabid: `${i}`, tabstyles: tabContainsErrors(i) ? tabStylesAddition : tabStyles, canDelete: true, render: () => (_jsx(DeltaInput, { signal: signal, index: i, onFocus: handleOnFocus })), children: _jsx(SignalFormTab, { onFocus: handleOnFocus }) }, `signalForm${i}`)))
            : [];
        const addSignalTab = (_jsx(Tab, { tabid: "addSignalTab", canDelete: false, tabstyles: newTabStyles, render: () => (_jsxs("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 1.5rem',
                    fontSize: '12px',
                    color: '#28ba62',
                }, children: [_jsx(FaPlus, { style: { display: 'inline-block' } }), _jsx("span", { style: { display: 'inline-block' }, children: "New Signal" })] })), children: _jsx(AddSignalFormTab, { onFocus: handleOnFocus, range: range, ref: newSignalFormRef, preferences: preferences }) }, "addSignalTab"));
        return [...signalTabs, addSignalTab];
    }, [handleOnFocus, preferences, range, tabContainsErrors, values.signals]);
    return (_jsxs("div", { children: [_jsx(SignalFormInfo, {}), _jsx(Tabs, { activeTab: values.activeTab, onClick: tapClickHandler, onDelete: handleDeleteSignal, children: signalFormTabs })] }));
}
export default memo(SignalsForm);
